import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { tg } from '../../lib/tg';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'md' | 'sm';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  /**
   * If true, trigger a light haptic on click (Telegram only). Default on
   * for primary buttons, off otherwise — tweak per intent.
   */
  haptic?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth,
    leading,
    trailing,
    haptic,
    className,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const shouldHaptic = haptic ?? variant === 'primary';
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      onClick={(e) => {
        if (shouldHaptic) tg.hapticLight();
        onClick?.(e);
      }}
      className={clsx(
        'pb-btn pb-focus-ring',
        variant === 'primary' && 'pb-btn-primary',
        variant === 'secondary' && 'pb-btn-secondary',
        variant === 'ghost' && 'pb-btn-ghost',
        variant === 'destructive' && 'pb-btn-destructive',
        size === 'sm' && 'min-h-0 px-3 py-2 text-[13px]',
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
});
